import React from 'react';

import coursePlanBlueprint from '@backrs_ui/grow-ui/src/assets/images/course-selection-guide/course-plan-blueprint.png';
import { GUIDE_TEXT_MAX_WIDTH } from '@backrs_ui/grow-ui/src/components/GuidePage';
import type { CourseSelectionBeyondCollege } from '@backrs_ui/grow-ui/src/pages/CourseSelectionGuide/content/types';

import { Box, SxProps, Theme, Typography } from '@mui/material';

type BeyondCollegeSectionProps = Readonly<{
  content: CourseSelectionBeyondCollege;
}>;

export const BeyondCollegeSection: React.FC<BeyondCollegeSectionProps> = ({ content }) => (
  <Box sx={containerSx}>
    <Typography variant="text-md-regular" sx={introSx}>
      {content.intro}
    </Typography>

    <Box sx={twoColumnSx}>
      <Box sx={listColumnSx}>
        {content.items.map((item) => (
          <Box key={item.label} sx={itemSx}>
            <Typography variant="text-md-semibold" sx={labelSx}>
              {item.label}
            </Typography>
            <Typography variant="text-sm-regular" sx={descriptionSx}>
              {item.description}
            </Typography>
          </Box>
        ))}
      </Box>
      <Box sx={imageColumnSx}>
        <Box
          component="img"
          src={coursePlanBlueprint}
          alt="Your course plan is your personal academic blueprint"
          sx={imageSx}
        />
      </Box>
    </Box>
  </Box>
);

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});

const introSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  maxWidth: GUIDE_TEXT_MAX_WIDTH,
});

const twoColumnSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  '@container (min-width: 720px)': {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
});

const listColumnSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  flex: 1,
});

const imageColumnSx: SxProps<Theme> = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const imageSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  maxWidth: 400,
  height: 'auto',
  borderRadius: backrs.radius['radius-xl'],
});

const itemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

const labelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const descriptionSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
